import { Trans, useLingui } from '@lingui/react/macro';
import type { BrandBrainIdentity, BrandBrainVoice, IcpSegment } from '@magig/schemas';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';
import { OnboardingExit, PageEyebrow } from '../components/auth-shells.js';
import { TeamReviewList, useReviewActions } from '../components/brain-review-lists.js';
import { Mag } from '../components/mag.js';
import {
  BrandCustomerSection,
  BrandIdentityBrainSection,
  BrandVoiceSection,
} from '../components/settings/sections.js';
import type { BrandBrainEnvelope, MeResponse } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { brandBrainQuery, brandsQuery, meQuery } from '../lib/queries.js';

// Brief review — the "reveal" pass. After Mag's research, this page is
// her unveiling what she learned about the brand: identity, voice,
// customer, team rendered as a showcase (positioning pull-quote, tone
// dials, ICP cards, the palette she pulled) rather than form fields.
// The forms stay hidden behind a quiet "tweak" toggle — they only
// appear when the founder wants to change something. Magig's cream/plum
// design language with staged motion so it reads as a reveal, not a
// settings page.
//
// Brief is strictly an onboarding/review step (the brand workbench owns
// ongoing edits), so the chrome is the OnboardingExit corner pill — no
// top nav, no back-link. The onboarding=1 flag is preserved across the
// brief steps so the next page (confirm) keeps the same treatment.

interface BriefSearch {
  onboarding?: '1' | undefined;
}

export const Route = createFileRoute('/brand/$slug/brief/')({
  validateSearch: (search: Record<string, unknown>): BriefSearch =>
    search['onboarding'] === '1' ? { onboarding: '1' } : {},
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const envelope = await queryClient.ensureQueryData(brandBrainQuery(brand.id));
    return { me, envelope, brand };
  },
  loader: ({ context }) =>
    context as {
      me: MeResponse;
      envelope: BrandBrainEnvelope | null;
      brand: { id: string; name: string; slug: string };
    },
  component: BriefPage,
});

type SectionKey = 'identity' | 'voice' | 'customer' | 'team';

function BriefPage() {
  const { t } = useLingui();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { brand } = Route.useLoaderData();
  const search = Route.useSearch();
  const isOnboarding = search.onboarding === '1';
  useBrandTitle(brand.slug, ['brief · review']);
  const { data: brands } = useQuery(brandsQuery());
  const { data: envelope } = useQuery(brandBrainQuery(brand.id));
  const liveBrands = brands?.filter((b) => b.archivedAt === null) ?? [];
  const showBrandLabel = liveBrands.length > 1;

  const brain = envelope?.brandBrain ?? null;
  const actions = useReviewActions(brand.id);

  // Which section is currently open for tweaking. null = pure reveal
  // mode (all showcases, no forms). Only one open at a time so the page
  // reads top-to-bottom without dueling forms.
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);
  const toggle = (key: SectionKey) => setOpenSection((cur) => (cur === key ? null : key));

  const palette = useMemo(() => {
    const colors = brain?.assets?.brand_kit?.colors ?? [];
    // De-dupe by hex so a brand that lists the same accent twice doesn't
    // tile the strip with repeats. Cap at 6 — past that it's noise.
    const seen = new Set<string>();
    const out: { name: string; hex: string }[] = [];
    for (const c of colors) {
      const hex = c.hex?.toLowerCase();
      if (!hex || seen.has(hex)) continue;
      seen.add(hex);
      out.push({ name: c.name, hex: c.hex });
      if (out.length >= 6) break;
    }
    return out;
  }, [brain]);

  const closeOnSave = async () => {
    setOpenSection(null);
    await queryClient.invalidateQueries({ queryKey: ['brand-brain'] });
  };

  const handleContinue = async () => {
    await navigate({
      to: '/brand/$slug/brief/confirm',
      params: { slug: brand.slug },
      search: isOnboarding ? { onboarding: '1' as const } : {},
    });
  };

  const companyName =
    brain?.identity?.company_name && brain.identity.company_name !== 'Untitled workspace'
      ? brain.identity.company_name
      : brand.name;

  return (
    <main className="relative min-h-screen overflow-hidden bg-[var(--cream)]">
      <OnboardingExit brandSlug={brand.slug} />

      <section className="relative mx-auto max-w-[760px] px-4 py-12 sm:px-6">
        {/* ── Hero: Mag presents the read ── */}
        <header className="flex flex-col items-start gap-4">
          <div
            className="mag-stagger-in flex items-center gap-3"
            style={{ animationDelay: '40ms' }}
          >
            <div className="relative">
              <div
                aria-hidden="true"
                className="mag-aura pointer-events-none absolute inset-[-14px] rounded-full"
              />
              <div className="mag-breathe relative">
                <Mag size={56} mood="casting" trackCursor />
              </div>
            </div>
            <PageEyebrow>
              <Trans>
                onboarding · step 2 of 6 · what i found
                {showBrandLabel ? ` · ${companyName.toLowerCase()}` : ''}
              </Trans>
            </PageEyebrow>
          </div>
          <h1
            className="mag-stagger-in font-display text-[40px] sm:text-[60px] font-black leading-[0.98] tracking-[-0.045em]"
            style={{ animationDelay: '140ms' }}
          >
            <Trans>
              here's {companyName.toLowerCase()},
              <br />
              <span className="text-[var(--plum)]">as i read it.</span>
            </Trans>
          </h1>
          <p
            className="mag-stagger-in max-w-[540px] font-serif text-xl leading-tight text-[var(--ink)]/80"
            style={{ animationDelay: '220ms' }}
          >
            <Trans>
              “this is what i pulled off your site. leave what's right — it stands. hit{' '}
              <span className="font-semibold text-[var(--ink)]">tweak</span> on anything i got
              wrong.”
            </Trans>
          </p>

          {palette.length > 0 ? (
            <div
              className="mag-stagger-in mt-1 flex flex-wrap items-center gap-2"
              style={{ animationDelay: '300ms' }}
            >
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/45">
                {'↘'} <Trans>your palette</Trans>
              </span>
              <div className="flex items-center gap-1.5">
                {palette.map((c) => (
                  <span
                    key={c.hex}
                    title={`${c.name} · ${c.hex}`}
                    className="h-5 w-5 rounded-full border border-[var(--ink)]/15 shadow-[1px_1px_0_rgba(0,0,0,0.06)]"
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </header>

        {/* ── The reveal: one section at a time, staged ── */}
        <div className="mt-10 flex flex-col gap-4">
          <RevealCard
            label={t`identity`}
            index={0}
            open={openSection === 'identity'}
            onToggle={() => toggle('identity')}
            showcase={<IdentityShowcase identity={brain?.identity ?? null} />}
          >
            <BrandIdentityBrainSection
              brand={brand}
              identity={brain?.identity}
              includeCompanyName
              saveLabel={t`save`}
              onAfterSave={closeOnSave}
            />
          </RevealCard>

          <RevealCard
            label={t`voice`}
            index={1}
            open={openSection === 'voice'}
            onToggle={() => toggle('voice')}
            showcase={<VoiceShowcase voice={brain?.voice ?? null} />}
          >
            <BrandVoiceSection
              brand={brand}
              voice={brain?.voice}
              saveLabel={t`save`}
              onAfterSave={closeOnSave}
            />
          </RevealCard>

          <RevealCard
            label={t`customer`}
            index={2}
            open={openSection === 'customer'}
            onToggle={() => toggle('customer')}
            showcase={<CustomerShowcase segments={brain?.customer?.icp_segments ?? []} />}
          >
            <BrandCustomerSection
              brand={brand}
              customer={brain?.customer}
              saveLabel={t`save`}
              onAfterSave={closeOnSave}
            />
          </RevealCard>

          <RevealCard
            label={t`team`}
            index={3}
            open={openSection === 'team'}
            onToggle={() => toggle('team')}
            showcase={<TeamShowcase team={brain?.team ?? []} />}
          >
            <TeamReviewList items={brain?.team ?? []} actions={actions} />
          </RevealCard>
        </div>

        <div
          className="mag-stagger-in mt-10 flex items-center justify-end"
          style={{ animationDelay: '640ms' }}
        >
          <button type="button" onClick={handleContinue} className="btn btn-lg">
            <Trans>looks good · continue</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>
    </main>
  );
}

// ─── RevealCard ────────────────────────────────────────────────────
// Showcase by default; the form is tucked behind a quiet "tweak"
// toggle. Staged in with mag-stagger-in so the sections cascade.

function RevealCard({
  label,
  index,
  open,
  onToggle,
  showcase,
  children,
}: {
  label: string;
  index: number;
  open: boolean;
  onToggle: () => void;
  showcase: ReactNode;
  children: ReactNode;
}) {
  return (
    <article
      className="mag-stagger-in rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] transition-shadow sm:p-6"
      style={{ animationDelay: `${360 + index * 80}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          ↘ {label}
        </p>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink)]/45 transition-colors hover:text-[var(--plum)]"
          aria-expanded={open}
        >
          {open ? <Trans>× close</Trans> : <Trans>tweak ✎</Trans>}
        </button>
      </div>

      {open ? (
        <div className="mag-fade-in mt-4 border-t border-[var(--line)] pt-4">{children}</div>
      ) : (
        <div className="mt-3">{showcase}</div>
      )}
    </article>
  );
}

// ─── Showcases ─────────────────────────────────────────────────────
// Read-only "here's what i found" renders. Honest about gaps — when a
// field is empty, prompt the founder to tweak rather than hiding it.

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <p className="font-serif text-[15px] italic leading-snug text-[var(--ink)]/50">{children}</p>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[var(--line)] bg-[var(--paper)] px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--ink)]/75">
      {children}
    </span>
  );
}

function IdentityShowcase({ identity }: { identity: BrandBrainIdentity | null }) {
  if (!identity) {
    return (
      <EmptyHint>
        <Trans>i couldn't sketch your identity yet — tweak to teach me.</Trans>
      </EmptyHint>
    );
  }
  const category = identity.category && identity.category !== 'Untitled' ? identity.category : null;
  const valueProps = (identity.value_props ?? []).filter((v) => v.trim().length > 0).slice(0, 4);
  const differentiators = (identity.differentiators ?? [])
    .filter((v) => v.trim().length > 0)
    .slice(0, 3);

  return (
    <div className="flex flex-col gap-4">
      {identity.positioning_statement ? (
        <p className="font-display text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-[-0.02em] text-[var(--ink)]">
          “{identity.positioning_statement}”
        </p>
      ) : (
        <EmptyHint>
          <Trans>no positioning line yet — tweak to give me one.</Trans>
        </EmptyHint>
      )}

      {category || identity.stage ? (
        <div className="flex flex-wrap gap-2">
          {category ? <Chip>{category}</Chip> : null}
          {identity.stage ? (
            <Chip>
              <Trans>{identity.stage} stage</Trans>
            </Chip>
          ) : null}
        </div>
      ) : null}

      {valueProps.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {valueProps.map((vp) => (
            <li
              key={vp}
              className="flex items-start gap-2 text-[14px] leading-[1.5] text-[var(--ink)]/85"
            >
              <span className="mt-[7px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--spell)]" />
              <span>{vp}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {differentiators.length > 0 ? (
        <p className="text-[13px] leading-[1.5] text-[var(--ink)]/65">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/45">
            <Trans>what sets you apart ·</Trans>{' '}
          </span>
          {differentiators.join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

const TONE_AXES: { key: keyof BrandBrainVoice['tone_axes']; left: string; right: string }[] = [
  { key: 'formal_casual', left: 'formal', right: 'casual' },
  { key: 'serious_playful', left: 'serious', right: 'playful' },
  { key: 'expert_friendly', left: 'expert', right: 'friendly' },
];

function ToneDial({ left, right, value }: { left: string; right: string; value: number }) {
  // value 0..10 → marker position. Clamp defensively.
  const pct = Math.max(0, Math.min(100, (value / 10) * 100));
  return (
    <div className="flex items-center gap-3">
      <span className="w-[58px] shrink-0 text-right font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
        {left}
      </span>
      <div className="relative h-1.5 flex-1 rounded-full bg-[var(--line)]">
        <span
          className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)]"
          style={{ left: `${pct}%` }}
        />
      </div>
      <span className="w-[58px] shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
        {right}
      </span>
    </div>
  );
}

function VoiceShowcase({ voice }: { voice: BrandBrainVoice | null }) {
  if (!voice) {
    return (
      <EmptyHint>
        <Trans>i haven't heard your voice yet — tweak to tune it.</Trans>
      </EmptyHint>
    );
  }
  const mustUse = (voice.must_use_words ?? []).filter((w) => w.trim().length > 0).slice(0, 6);
  const mustAvoid = (voice.must_avoid_words ?? []).filter((w) => w.trim().length > 0).slice(0, 6);
  const ax = voice.tone_axes;

  return (
    <div className="flex flex-col gap-4">
      {ax ? (
        <div className="flex flex-col gap-2.5">
          {TONE_AXES.map((t) => (
            <ToneDial key={t.key} left={t.left} right={t.right} value={ax[t.key] ?? 5} />
          ))}
        </div>
      ) : (
        <EmptyHint>
          <Trans>tone signal is thin — tweak to nudge me.</Trans>
        </EmptyHint>
      )}

      {mustUse.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--sage)]">
            <Trans>say</Trans>
          </span>
          {mustUse.map((w) => (
            <span
              key={w}
              className="rounded-full bg-[var(--sage)]/15 px-2.5 py-1 text-[12px] font-medium text-[var(--ink)]/80"
            >
              {w}
            </span>
          ))}
        </div>
      ) : null}

      {mustAvoid.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/40">
            <Trans>never</Trans>
          </span>
          {mustAvoid.map((w) => (
            <span
              key={w}
              className="rounded-full border border-[var(--line)] px-2.5 py-1 text-[12px] text-[var(--ink)]/45 line-through"
            >
              {w}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CustomerShowcase({ segments }: { segments: IcpSegment[] }) {
  const real = segments.filter((s) => s.name?.trim().length > 0).slice(0, 2);
  if (real.length === 0) {
    return (
      <EmptyHint>
        <Trans>i couldn't pin down who you sell to — tweak to teach me.</Trans>
      </EmptyHint>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {real.map((seg) => {
        const pains = (seg.pains ?? []).filter((p) => p.trim().length > 0).slice(0, 3);
        return (
          <div
            key={seg.name}
            className="rounded-xl border border-[var(--line)] bg-[var(--paper)]/50 p-3.5"
          >
            <p className="font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]">
              {seg.name}
            </p>
            {seg.description ? (
              <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/70">
                {seg.description}
              </p>
            ) : null}
            {pains.length > 0 ? (
              <p className="mt-2 text-[12.5px] leading-[1.5] text-[var(--ink)]/65">
                <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--pulse)]">
                  <Trans>pain ·</Trans>{' '}
                </span>
                {pains.join(' · ')}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function TeamShowcase({ team }: { team: { name: string; role?: string | null }[] }) {
  const real = team.filter((m) => m.name?.trim().length > 0).slice(0, 6);
  if (real.length === 0) {
    return (
      <EmptyHint>
        <Trans>no team page on your site — tweak to add the people behind this.</Trans>
      </EmptyHint>
    );
  }
  return (
    <div className="flex flex-wrap gap-3">
      {real.map((m) => (
        <div key={m.name} className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--spell)] font-mono text-[11px] font-bold text-[var(--ink)]">
            {initialsOf(m.name)}
          </span>
          <div className="leading-tight">
            <p className="text-[13px] font-semibold text-[var(--ink)]">{m.name}</p>
            {m.role ? <p className="text-[11px] text-[var(--ink)]/55">{m.role}</p> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
